/**
 * Что Mini App спрашивает у `study-api`.
 *
 * Своего клиента здесь нет — только адреса: разбор `401`/`403`, повторы и
 * единый экран отказа живут в каркасе платформы (`apiFetch`, §13.2), и
 * второй такой же в сервисе означал бы два разных поведения при отказе.
 *
 * Подписанная строка Telegram прикладывается каркасом автоматически ко
 * всем запросам (`shell/miniapp.ts`), поэтому здесь её не видно и трогать
 * её не нужно.
 */

import { apiFetch } from '@shell/api';
import type { Me } from '@shell/api';

/** Раздел приложения: справочник, техкарты, обучение… (§7.2). */
export interface SectionView {
  code: string;
  name: string;
}

/**
 * Ответ `/api/study/me`. Первые пять полей — контракт `Me` каркаса (он
 * рисует шапку), остальное своё.
 */
export interface StudyMe extends Me {
  /** Копировать (D233). Нет — замок и водяной знак. */
  can_copy?: boolean;
  level: string;
  position_id: number | null;
  sections: SectionView[];
  /** Почему показывать нечего: нет должности или у неё нет разделов. */
  problem: string | null;
  restaurant_tz: string;
  /** Права в сервисе — «объект:действие» (D181, §8.7 ТЗ платформы). */
  rights?: string[];
}

/** Бейдж аллергена: код, человеческое название и значок. */
export interface AllergenBadge {
  code: string;
  name: string;
  icon: string | null;
}

/** Строка результата поиска. Бейджи приходят вместе с ней — до открытия
 *  карточки (§16.2, пункт 10): официант у стола отвечает про орехи, не
 *  делая второго тапа. */
export interface DishRow {
  id: number;
  name: string;
  menu: string;
  category: string | null;
  price: number | null;
  allergens: AllergenBadge[];
  /** Готовая строка про аллергены. Считает её сервер: «Состав не
   *  размечен» и «Аллергенов нет» — разные ответы, и приложение не имеет
   *  права выбирать между ними само. */
  allergen_summary: string;
  composition_known: boolean;
  has_photo: boolean;
  /** Демонстрационная запись (D90): состав и аллергены вымышлены.
   *  Приходит вместе со строкой, как и бейджи: официант у стола читает
   *  строку и идёт отвечать гостю, не открывая карточку. */
  is_demo: boolean;
  /** Текст плашки. Как и строку про аллергены, его пишет сервер — чтобы
   *  бот и приложение не начали формулировать по-разному (§8.7). */
  demo_badge: string | null;
}

export interface DishSearch {
  dishes: DishRow[];
  suggestions: string[];
  /** Пояснение к выдаче фильтра «без X» — почему список короче ожидаемого. */
  note: string | null;
  empty_text: string | null;
}

export interface DishCard {
  id: number;
  name: string;
  menu: string;
  category: string | null;
  guest_text: string;
  serving_notes: string | null;
  pairing_notes: string | null;
  price: number | null;
  ingredients: { name: string; allergens: string[]; reviewed: boolean }[] | null;
  composition_text: string | null;
  allergens: AllergenBadge[];
  allergen_summary: string;
  composition_known: boolean;
  photos: string[];
  tech_card_available: boolean;
  is_demo: boolean;
  /** Полоса над карточкой. Пустая — у настоящего блюда. */
  demo_notice: string | null;
}

/** Ответ `/api/study/demo`. `banner` уже готовой строкой: формулировку
 *  выбирает сервер — одну на бота и на приложение (§8.7). */
export interface DemoState {
  dishes: boolean;
  courses: boolean;
  banner: string | null;
}

export interface TechCard {
  dish_id: number;
  version: number;
  body: string;
  valid_from: string | null;
}

export const studyApi = {
  me: () => apiFetch<StudyMe>('/api/study/me'),
  sections: () =>
    apiFetch<{ position_id: number | null; problem: string | null; sections: SectionView[] }>(
      '/api/study/sections',
    ),
  openSection: (code: string) =>
    apiFetch<{ code: string; items: unknown[]; note: string }>(`/api/study/sections/${code}`),
  allergens: () =>
    apiFetch<{ allergens: (AllergenBadge & { id: number })[] }>('/api/study/allergens'),
  searchDishes: (query: string, without: string[]) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (without.length > 0) params.set('without', without.join(','));
    const suffix = params.toString();
    return apiFetch<DishSearch>(`/api/study/dishes${suffix ? `?${suffix}` : ''}`);
  },
  dish: (id: number) => apiFetch<DishCard>(`/api/study/dishes/${id}`),
  /** Есть ли в сервисе демонстрационные данные и каким текстом о них
   *  сказать (D90). Спрашивается один раз за сеанс: они появляются и
   *  исчезают командой в консоли, а не в течение смены. */
  demoState: () => apiFetch<DemoState>('/api/study/demo'),
  techCard: (id: number) => apiFetch<TechCard>(`/api/study/dishes/${id}/tech-card`),

  /* ── обучение (§9) ─────────────────────────────────────────────────── */
  courses: () => apiFetch<MyCourses>('/api/study/courses'),
  coursePlan: (id: number) => apiFetch<CoursePlan>(`/api/study/courses/${id}`),
  lesson: (id: number) => apiFetch<LessonView>(`/api/study/lessons/${id}`),
  markRead: (id: number) =>
    apiFetch<{ lesson_id: number; status: string }>(`/api/study/lessons/${id}/read`, {
      method: 'POST',
    }),

  /* ── тесты (§9.2–§9.4) ─────────────────────────────────────────────── */
  openTest: (lessonId: number) => apiFetch<TestView>(`/api/study/lessons/${lessonId}/test`),
  answer: (attemptId: number, questionId: number, chosen: number[]) =>
    apiFetch<{ saved: boolean }>(`/api/study/attempts/${attemptId}/answers`, {
      method: 'POST',
      body: { question_id: questionId, chosen_options: chosen },
    }),
  finishTest: (attemptId: number) =>
    apiFetch<TestResult>(`/api/study/attempts/${attemptId}/finish`, { method: 'POST' }),
  myProgress: () => apiFetch<ReportView>('/api/study/reports/my-progress'),

  /* ── отчёты управляющего и владельца (§12.2) ───────────────────────── */
  progressReport: () => apiFetch<ReportView>('/api/study/admin/reports/progress'),
  weakTopicsReport: () => apiFetch<ReportView>('/api/study/admin/reports/weak-topics'),
  activityReport: () => apiFetch<ReportView>('/api/study/admin/reports/activity'),
  assistantReport: () => apiFetch<ReportView>('/api/study/admin/reports/assistant'),
  mentorPanel: () => apiFetch<MentorPanel>('/api/study/admin/reports/mentor-panel'),
  ownerSummary: () =>
    apiFetch<{ code: string; title: string; text: string }>('/api/study/admin/reports/owner-summary'),

  /* ── наставник (§10.9, этап S8) ────────────────────────────────────── */
  mentorMetrics: () => apiFetch<MentorMetrics>('/api/study/mentor/metrics'),

  /* ── ролевые тренировки (§10.12, этап S10) ─────────────────────────── */
  roleplayHome: () => apiFetch<RoleplayHome>('/api/study/mentor/roleplay'),
  roleplayStart: (scenarioId: number) =>
    apiFetch<RoleplaySession>('/api/study/mentor/roleplay/start', {
      method: 'POST',
      body: { scenario_id: scenarioId },
    }),
  roleplaySession: (id: number) => apiFetch<RoleplaySession>(`/api/study/mentor/roleplay/${id}`),
  roleplayReply: (id: number, text: string) =>
    apiFetch<RoleplaySession>(`/api/study/mentor/roleplay/${id}/reply`, {
      method: 'POST',
      body: { text },
    }),
  roleplayFinish: (id: number) =>
    apiFetch<RoleplaySession>(`/api/study/mentor/roleplay/${id}/finish`, { method: 'POST' }),

  /* ── цели роста и микроурок (§10.13, §10.14, этап S11) ─────────────── */
  goalsHome: () => apiFetch<GoalsHome>('/api/study/mentor/goals'),
  setGoal: (metricCode: string) =>
    apiFetch<GoalView>('/api/study/mentor/goals', {
      method: 'POST',
      body: { metric_code: metricCode },
    }),
  cancelGoal: (id: number) =>
    apiFetch<{ id: number; status: string }>(`/api/study/mentor/goals/${id}/cancel`, {
      method: 'POST',
    }),
  microToday: () => apiFetch<{ delivery: MicroDelivery | null }>('/api/study/mentor/micro'),
  answerMicro: (deliveryId: number, option: number) =>
    apiFetch<{ text: string }>(`/api/study/mentor/micro/${deliveryId}/answer`, {
      method: 'POST',
      body: { option },
    }),
  adminMicroLessons: () => apiFetch<{ lessons: AdminMicroLesson[] }>('/api/study/admin/micro-lessons'),
  publishMicro: (id: number) =>
    apiFetch<{ id: number; status: string }>(`/api/study/admin/micro-lessons/${id}/publish`, {
      method: 'POST',
    }),
  unpublishMicro: (id: number) =>
    apiFetch<{ id: number; status: string }>(`/api/study/admin/micro-lessons/${id}/unpublish`, {
      method: 'POST',
    }),

  /* ── сценарии тренировок, право «Курсы · править» ─────────────────── */
  adminScenarios: () => apiFetch<{ scenarios: AdminScenario[] }>('/api/study/admin/scenarios'),
  updateScenario: (id: number, payload: ScenarioPayload) =>
    apiFetch<{ id: number; status: string }>(`/api/study/admin/scenarios/${id}`, {
      method: 'PUT',
      body: payload,
    }),
  publishScenario: (id: number) =>
    apiFetch<{ id: number; status: string }>(`/api/study/admin/scenarios/${id}/publish`, {
      method: 'POST',
    }),
  unpublishScenario: (id: number) =>
    apiFetch<{ id: number; status: string }>(`/api/study/admin/scenarios/${id}/unpublish`, {
      method: 'POST',
    }),

  /* ── админка (§11), уровень admin ─────────────────────────────────── */
  adminDishes: () => apiFetch<AdminDishes>('/api/study/admin/dishes'),
  createDish: (payload: DishPayload) =>
    apiFetch<{ dish_id: number }>('/api/study/admin/dishes', { method: 'POST', body: payload }),
  editDish: (id: number, payload: DishPayload) =>
    apiFetch<{ editing_id: number; is_edit_draft: boolean }>(`/api/study/admin/dishes/${id}`, {
      method: 'PUT',
      body: payload,
    }),
  publishDish: (id: number) =>
    apiFetch<{ dish_id: number }>(`/api/study/admin/dishes/${id}/publish`, { method: 'POST' }),
  deleteDish: (id: number) =>
    apiFetch<{ deleted: boolean }>(`/api/study/admin/dishes/${id}`, { method: 'DELETE' }),
  incompleteReport: () => apiFetch<IncompleteReport>('/api/study/admin/reports/incomplete'),

  /* ── курсы: рабочее место управляющего (§9.5, §11.2) ──────────────── */
  adminCourses: () => apiFetch<AdminCourses>('/api/study/admin/courses'),
  createCourse: (payload: CoursePayload) =>
    apiFetch<{ course_id: number; status: string }>('/api/study/admin/courses', {
      method: 'POST',
      body: payload,
    }),
  createModule: (courseId: number, title: string, sortOrder: number) =>
    apiFetch<{ module_id: number }>(`/api/study/admin/courses/${courseId}/modules`, {
      method: 'POST',
      body: { title, sort_order: sortOrder },
    }),
  createLesson: (moduleId: number, payload: LessonPayload) =>
    apiFetch<{ lesson_id: number }>(`/api/study/admin/modules/${moduleId}/lessons`, {
      method: 'POST',
      body: payload,
    }),
  publishCourse: (courseId: number) =>
    apiFetch<{ course_id: number; status: string }>(
      `/api/study/admin/courses/${courseId}/publish`,
      { method: 'POST' },
    ),
  /** Назначить должности. Человеку поимённо — тем же адресом, но это
   *  редкий случай, и экран его пока не показывает (§9.5). */
  assignCourse: (
    courseId: number,
    positionId: number,
    isMandatory: boolean,
    dueDays: number | null = null,
  ) =>
    apiFetch<{ assignment_id: number }>(`/api/study/admin/courses/${courseId}/assign`, {
      method: 'POST',
      body: { position_id: positionId, is_mandatory: isMandatory, due_days: dueDays },
    }),
  unassign: (assignmentId: number) =>
    apiFetch<{ assignment_id: number; removed: boolean }>(
      `/api/study/admin/assignments/${assignmentId}`,
      { method: 'DELETE' },
    ),

  /* ── правка курса на месте (D98, §11.5) ───────────────────────────── */
  courseContent: (courseId: number) =>
    apiFetch<CourseContent>(`/api/study/admin/courses/${courseId}/content`),
  editCourse: (courseId: number, payload: CoursePayload) =>
    apiFetch<{ course_id: number }>(`/api/study/admin/courses/${courseId}`, {
      method: 'PUT',
      body: payload,
    }),
  /** Урок в конец курса; модуль сервер заводит сам. */
  addLesson: (courseId: number, payload: LessonEdit) =>
    apiFetch<{ lesson_id: number }>(`/api/study/admin/courses/${courseId}/lessons`, {
      method: 'POST',
      body: payload,
    }),
  editLesson: (lessonId: number, payload: LessonEdit) =>
    apiFetch<{ lesson_id: number }>(`/api/study/admin/lessons/${lessonId}`, {
      method: 'PUT',
      body: payload,
    }),
  deleteLesson: (lessonId: number) =>
    apiFetch<{ removed: boolean }>(`/api/study/admin/lessons/${lessonId}`, { method: 'DELETE' }),
  addQuestion: (lessonId: number, payload: QuestionEdit) =>
    apiFetch<{ question_id: number }>(`/api/study/admin/lessons/${lessonId}/questions`, {
      method: 'POST',
      body: payload,
    }),
  editQuestion: (questionId: number, payload: QuestionEdit) =>
    apiFetch<{ question_id: number }>(`/api/study/admin/questions/${questionId}`, {
      method: 'PUT',
      body: payload,
    }),
  deleteQuestion: (questionId: number) =>
    apiFetch<{ removed: boolean }>(`/api/study/admin/questions/${questionId}`, {
      method: 'DELETE',
    }),

  /* ── ИИ-помощник (§10) ────────────────────────────────── */
  ask: (question: string) =>
    apiFetch<AssistantReply>('/api/study/assistant/ask', { method: 'POST', body: { question } }),

  /* ── база знаний, уровень admin (§10.4, §10.6) ───────────── */
  documents: () => apiFetch<{ documents: DocumentRow[] }>('/api/study/admin/documents'),
  /**
   * Загрузка регламента. Файл уходит телом как есть, имя и название —
   * заголовками: тот же приём, что у импорта блюд и у фотографий.
   *
   * Название кодируется, потому что заголовок HTTP не переносит
   * кириллицу; сервер его раскодирует. Без этого все регламенты
   * назывались бы «%D0%A1%D0%B0%D0%BD…».
   */
  uploadDocument: (file: File, title: string) =>
    apiFetch<{ document_id: number; title: string; chunks: number; characters: number }>(
      '/api/study/admin/documents',
      {
        method: 'POST',
        raw: file,
        headers: {
          'X-Filename': encodeURIComponent(file.name),
          'X-Title': encodeURIComponent(title || file.name),
        },
      },
    ),
  reindexDocument: (id: number) =>
    apiFetch<{ document_id: number; chunks: number }>(
      `/api/study/admin/documents/${id}/reindex`,
      { method: 'POST' },
    ),
  archiveDocument: (id: number) =>
    apiFetch<{ document_id: number; status: string }>(
      `/api/study/admin/documents/${id}/archive`,
      { method: 'POST' },
    ),
  gaps: (status: string) =>
    apiFetch<{ gaps: GapRow[] }>(`/api/study/admin/gaps?status=${encodeURIComponent(status)}`),
  answerGap: (id: number, answer: string) =>
    apiFetch<{ gap_id: number; status: string }>(`/api/study/admin/gaps/${id}/answer`, {
      method: 'POST',
      body: { answer },
    }),
  dismissGap: (id: number) =>
    apiFetch<{ gap_id: number; status: string }>(`/api/study/admin/gaps/${id}/dismiss`, {
      method: 'POST',
    }),
  stopTopics: () => apiFetch<{ stop_topics: StopTopicRow[] }>('/api/study/admin/stop-topics'),
};

export interface CourseRow {
  id: number;
  title: string;
  description: string | null;
  is_mandatory: boolean;
  passed: number;
  total: number;
  is_complete: boolean;
  /** Срок (D99): дата и готовая фраза сервера; у курса без срока — `null`. */
  due_on?: string | null;
  due_text?: string | null;
  /** Демонстрационный курс (D90): написан для показа и руководством не
   *  утверждён. Видно до открытия — начинать выдуманный курс как
   *  обязательный человек не должен. */
  is_demo: boolean;
  demo_badge: string | null;
}

export interface MyCourses {
  courses: CourseRow[];
  /** Статус обязательного обучения словами. Формулировку выбирает сервер:
   *  «не допущен» в ней не появится никогда (D58) — решение о допуске
   *  принимает человек и вне сервиса. */
  attestation: string;
  empty_text: string | null;
}

export interface LessonRow {
  id: number;
  title: string;
  status: string;
  is_locked: boolean;
  has_test: boolean;
  /** Почему урок закрыт. Замок без объяснения читается как поломка. */
  locked_text: string | null;
}

export interface CoursePlan {
  course_id: number;
  lessons: LessonRow[];
  is_demo: boolean;
  demo_notice: string | null;
}

export interface TestQuestion {
  id: number;
  kind: string;
  body: string;
  /** Только тексты и идентификаторы: какой вариант верный, знает сервер. */
  options: { id: number; body: string }[];
  chosen: number[];
}

export interface TestView {
  attempt_id: number | null;
  answered?: number;
  questions: TestQuestion[];
  /** «Тест не составлен» — состояние наполнения, а не ошибка. */
  note: string | null;
}

export interface TestResult {
  score: number;
  passed: boolean;
  pass_score: number;
  attempts_made: number;
  best_score: number;
  mistakes: { question: string; explanation: string | null; correct: string[] }[];
}

export interface ReportView {
  code: string;
  title: string;
  rows: Record<string, unknown>[];
  note: string | null;
}

/** Строка панели наставника `S7`: числа приходят готовыми строками. */
export interface MentorPanelRow {
  employee_id: number;
  employee: string;
  position: string | null;
  zone: string | null;
  before: string | null;
  after: string | null;
  goal: { title: string; status: string; text: string } | null;
  trainings: number;
  training_score: string | null;
  micro_sent: number;
  micro_answered: number;
  micro_correct: number;
  lessons_passed: number;
}

export interface MentorPanel {
  code: 'S7';
  title: string;
  day: string;
  note: string;
  issues: string[];
  rows: MentorPanelRow[];
}

export interface LessonView {
  id: number;
  title: string;
  body: string;
  video_url: string | null;
  has_test: boolean;
  test_note: string | null;
  is_demo: boolean;
  demo_notice: string | null;
}

/** Строка списка админки: состояние карточки и её пробелы сразу видны —
 *  иначе «что не заполнено» приходится искать отдельным отчётом на каждое
 *  блюдо. */
export interface AdminDishRow {
  id: number;
  name: string;
  status: string;
  draft_of_id: number | null;
  menu_name: string;
  photos: number;
  ingredients: number;
  has_text: boolean;
  /** Демонстрационная карточка: управляющему видно, что её можно убрать
   *  командой `make demo-study-clear`, а не править руками. */
  is_demo: boolean;
}

export interface AdminDishes {
  dishes: AdminDishRow[];
  menus: { id: number; code: string; name: string }[];
}

/** Кому назначен курс. `title` — название должности или имя человека:
 *  экран читает управляющий, и «должность 7» ему не говорит ничего. */
export interface CourseAssignment {
  id: number;
  position_id: number | null;
  employee_id: number | null;
  is_mandatory: boolean;
  /** Срок в днях (D99) или `null`. */
  due_days?: number | null;
  title: string;
}

/** Курс в «Редакторе». `lessons` — сколько в нём уроков: курс без уроков
 *  назначать нечего, и это должно быть видно до назначения. */
export interface AdminCourseRow {
  id: number;
  title: string;
  description: string | null;
  status: string;
  pass_score: number;
  is_demo: boolean;
  lessons: number;
  assignments: CourseAssignment[];
}

export interface LessonEdit {
  title: string;
  body: string;
  video_url?: string | null;
}

export interface QuestionEdit {
  kind?: 'single' | 'multi';
  body: string;
  explanation?: string | null;
  /** [текст варианта, правильный ли]. Не передан при правке — варианты не
   *  трогаются (D98). */
  options?: [string, boolean][];
}

export interface EditorQuestion {
  id: number;
  kind: string;
  body: string;
  explanation: string | null;
  /** Сколько раз отвечали: больше нуля — удалить и сменить варианты нельзя. */
  answers: number;
  options: { body: string; is_correct: boolean }[];
}

export interface EditorLesson {
  id: number;
  title: string;
  body: string;
  video_url: string | null;
  /** Есть прохождения или попытки — урок не удаляется (D98). */
  has_results: boolean;
  questions: EditorQuestion[];
}

export interface CourseContent {
  course: { id: number; title: string; description: string | null; pass_score: number; status: string };
  lessons: EditorLesson[];
}

export interface AdminCourses {
  courses: AdminCourseRow[];
  /** Должности ресторана — для назначения. Приходят тем же ответом: на
   *  телефоне лишний запрос виден. */
  positions: { id: number; code: string; name: string }[];
}

export interface CoursePayload {
  title: string;
  description?: string | null;
  /** Ниже 70 отклоняет СУБД (§9.3) — своей проверки здесь нет намеренно. */
  pass_score?: number;
}

export interface LessonPayload {
  title: string;
  body: string;
  sort_order: number;
  video_url?: string | null;
  has_test?: boolean;
}

export interface DishPayload {
  name: string;
  menu_id: number;
  guest_text?: string | null;
  price?: string | null;
}

export interface IncompleteReport {
  dishes: { id: number; name: string; no_photo: boolean; no_composition: boolean; no_text: boolean }[];
  unreviewed_ingredients: { id: number; name: string }[];
  total_dishes: number;
}

/* ── ИИ-помощник и база знаний (§10) ────────────────────────────────── */

/** Документ-источник ответа. Ссылка на него — обязательная часть ответа
 *  (§10.1): помощник без источника неотличим от выдумки. */
export interface AssistantSource {
  document_id: number;
  title: string;
  /** Место в документе — пункт, раздел. Может отсутствовать. */
  anchor: string | null;
}

/**
 * Ответ помощника.
 *
 * `text` приходит с сервера готовым и **не собирается здесь по частям**.
 * Формулировки «передал вопрос управляющему», «в наших документах этого
 * нет», срок ответа из D57 — нормативные тексты §10.4–§10.5, и второй их
 * набор в браузере означал бы, что бот и приложение обещают человеку
 * разное.
 */
export interface AssistantReply {
  text: string;
  /** Чем кончился вопрос: `answer` · `stop_topic` · `catalog` · `gap` ·
   *  `unavailable`. Нужен приложению ровно для одного — понять, чем
   *  помочь дальше: увести в справочник, показать источники, промолчать. */
  kind: string;
  /** Вопрос ушёл человеку. Ждать ответа здесь не нужно — он придёт лично. */
  escalated: boolean;
  sources: AssistantSource[];
  /** Код сработавшей стоп-темы (D54) или `null`. */
  stop_topic: string | null;
}

/** Регламент в базе знаний. `indexed` — проиндексирован ли: без этого
 *  документ загружен, но в ответах не участвует. */
export interface DocumentRow {
  id: number;
  title: string;
  version: number;
  status: string;
  mime: string;
  indexed: boolean;
  uploaded_at: string;
  /** Демонстрационный регламент (D90). Слово «ДЕМО» есть и в названии,
   *  и в каждом фрагменте: цитата в ответе помощника уходит вместе с
   *  ним (D91). */
  is_demo: boolean;
}

/** Пробел — вопрос, на который база не ответила (§10.4). Ответ
 *  администратора становится частью базы и достаётся следующему. */
export interface GapRow {
  id: number;
  question: string;
  asked_at: string;
  status: string;
  answer: string | null;
}

/** Стоп-тема (D54): о чём помощник не рассуждает и что делает вместо
 *  этого. Правится в базе, а не в коде, — потому и показывается списком. */
export interface StopTopicRow {
  code: string;
  name: string;
  behaviour: string;
  is_urgent: boolean;
  patterns: string[];
}

/**
 * Один показатель официанта со всеми сравнениями (§10.9).
 *
 * **Каждое поле сравнения может быть `null`, и причины разные.**
 * `previous` пуст, пока нет второй смены; `team_best` — пока в должности
 * меньше пяти человек с данными (D74), и тогда `best_hidden` объясняет,
 * почему; `target` — пока норматив не задан управляющим (`Н32`). Экран
 * обязан различать эти случаи: «нет данных» и «ноль» — разные сообщения.
 *
 * **Имени рядом с лучшим результатом нет и быть не может.** Его нет даже
 * в ответе сервера — колонки владельца нет уже в таблице (D67).
 *
 * Числа приходят строками: `Decimal` на сервере в JSON стал бы `float` и
 * потерял копейки. Интерфейс их только показывает — считает `finance`.
 */
export interface MentorMetric {
  code: string;
  title: string;
  unit: string;
  value: string | null;
  delta: string | null;
  previous: string | null;
  team_avg: string | null;
  team_best: string | null;
  target: string | null;
  group_size: number;
  best_hidden: boolean;
}

/* ── ролевые тренировки (§10.12) ─────────────────────────────────────── */

/** Квота D71 на сейчас. `text` — готовая строка сервера: и «осталось две»,
 *  и «на этой неделе закончились, а пока можно…» формулирует он. */
export interface RoleplayQuota {
  can_start: boolean;
  week_left: number;
  week_limit: number;
  /** Квота снижена лестницей бюджета (§14.4). */
  reduced: boolean;
  text: string;
}

export interface RoleplayTurn {
  ord: number;
  /** `agent` — гость (его играет модель), `employee` — сам сотрудник. */
  role: 'agent' | 'employee';
  body: string;
}

/**
 * Тренировка глазами её участника. Сервер отдаёт её только ему самому:
 * чужой разговор по прямому адресу — `404` (D64).
 */
export interface RoleplaySession {
  id: number;
  scenario: { id: number; name: string; success_note: string | null };
  turns: RoleplayTurn[];
  finished: boolean;
  turns_left: number;
  replies_made: number;
  min_replies_for_score: number;
  score: { values: Record<string, number>; average: number; feedback: string | null } | null;
  /** Итог словами: разбор и счётчики «проходили раньше» (§10.16). */
  summary: string[];
  /** Пометка шлюза: «упрощённый ответ, лимит исчерпан». */
  note: string | null;
}

export interface RoleplayHome {
  available: boolean;
  reason: string;
  /** §10.15 дословно — показывается при входе в раздел. */
  privacy_notice: string;
  criteria: { code: string; title: string }[];
  max_turns: number;
  quota: RoleplayQuota | null;
  scenarios: { id: number; name: string; success_note: string | null }[];
  open_session: RoleplaySession | null;
  /** Сценарий, дата и средний балл — без реплик. */
  history: {
    session_id: number;
    scenario: string;
    started_at: string;
    finished: boolean;
    average: number | null;
  }[];
}

/* ── цели роста и микроурок (§10.13, §10.14) ─────────────────────────── */

export interface GoalView {
  id: number;
  metric_code: string;
  title: string;
  unit: string;
  baseline: string | null;
  target: string | null;
  shifts_planned: number;
  status: 'active' | 'reached' | 'missed' | 'cancelled';
  set_at: string;
  measured_value: string | null;
  /** Итог словами сервера — без «провалена» (§10.13). */
  result_text: string | null;
  description: string;
}

export interface GoalSuggestion {
  metric_code: string;
  title: string;
  unit: string;
  current: string | null;
  team_avg: string | null;
  target: string | null;
}

export interface GoalsHome {
  available: boolean;
  reason: string;
  suggestions: GoalSuggestion[];
  active: GoalView[];
  history: GoalView[];
  max_active: number;
}

export interface MicroDelivery {
  id: number;
  sent_on: string;
  answered_correctly: boolean | null;
  topic: string;
  body: string;
  good_phrases: string[];
  avoid_phrases: string[];
  question: string | null;
  options: string[];
}

export interface AdminMicroLesson {
  id: number;
  topic: string;
  body: string;
  good_phrases: string[];
  avoid_phrases: string[];
  question: string | null;
  options: string[];
  /** Показатель, по цели на который урок выбирается; `null` — общий. */
  metric_title: string | null;
  status: string;
}

export interface AdminScenario {
  id: number;
  code: string;
  name: string;
  guest_brief: string;
  success_note: string | null;
  status: string;
}

export interface ScenarioPayload {
  name: string;
  guest_brief: string;
  success_note: string | null;
}

/** Экран «Мои показатели» целиком. `available: false` — показателей нет,
 *  и `reason` называет причину словами сервера. */
export interface MentorMetrics {
  available: boolean;
  reason: string;
  /** Метка актуальности — готовой строкой (§10.9, правило 2). */
  freshness: string;
  stale?: boolean;
  day?: string | null;
  checks?: number | null;
  metrics: MentorMetric[];
  min_group_for_best: number;
}
